import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import { BASE_URL } from "../Apis/apiConfig";
import "../Style/Archive.css";

import { Building2, Users, RefreshCw, AlertCircle, FileText, ChevronRight, Filter, X, Plus, Search, Download, } from "lucide-react";

const Archive = () => {
  const navigate = useNavigate();
  const { role } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [membersData, setMembersData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedSociety, setSelectedSociety] = useState(null);
  const [viewMode, setViewMode] = useState("society");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("service");
  const [tabs, setTabs] = useState([
    { id: "service", label: "Service", icon: <Building2 size={16} /> },
    { id: "society", label: "Society", icon: <Users size={16} /> },
  ]);

  const societyName = localStorage.getItem("society") || "";
  const [employeesCache, setEmployeesCache] = useState(null);
  const [rolesCache, setRolesCache] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const paramsObj = {};
      if (societyName) paramsObj.societyName = societyName;

      const params = new URLSearchParams(paramsObj).toString();
      const url = `${BASE_URL}/api/employee/get-archive-employee${
        params ? `?${params}` : ""
      }`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        let msg = `Server error ${res.status}`;
        try {
          const errJson = await res.json();
          msg = errJson.message || JSON.stringify(errJson);
        } catch (e) {}
        throw new Error(msg);
      }

      const json = await res.json();
      const data = Array.isArray(json) ? json : json.data ?? json.result ?? [];

      setEmployeesCache(data);

      const uniqCompanies = Array.from(
        new Set(
          data
            .map((d) => (d.companyName ?? "").toString().trim())
            .filter(Boolean)
        )
      );
      setCompanies(uniqCompanies);

      const uniqSocietiesFromEmployees = Array.from(
        new Set(
          data
            .map((d) => (d.societyName ?? "").toString().trim())
            .filter(Boolean)
        )
      );

      if (activeTab === "service") {
        setMembersData(data);
        setFilteredData(data);
        setSocieties(uniqSocietiesFromEmployees);
      }

      return data;
    } catch (err) {
      console.error("Fetch employees error:", err);
      setError(err.message || "Failed to fetch employees");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token") || "";
      if (!societyName) {
        setError("Missing societyName");
        setLoading(false);
        return [];
      }

      const url = `${BASE_URL}/api/assign-role/get-archive-role-by-society-name?societyName=${encodeURIComponent(
        societyName
      )}`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(
          `Server responded ${res.status}: ${txt || res.statusText}`
        );
      }

      const json = await res.json();
      const data = Array.isArray(json) ? json : json.data ?? json.result ?? [];

      setRolesCache(data);
      const uniqSocieties = Array.from(
        new Set(data.map((d) => d.societyName).filter(Boolean))
      );
      setSocieties(uniqSocieties);

      if (activeTab === "society") {
        setMembersData(data);
        setFilteredData(data);
      }

      return data;
    } catch (err) {
      console.error("Fetch roles error:", err);
      setError(err.message || "Failed to fetch roles");
      return [];
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const controller = new AbortController();

    async function loadInitialData() {
      await fetchEmployees();
      await fetchRoles();
    }

    loadInitialData();

    return () => controller.abort();
  }, []); 


  useEffect(() => {
    if (activeTab === "service") {
      if (employeesCache) {
        setMembersData(employeesCache);
        setFilteredData(employeesCache);
      } else {
        fetchEmployees();
      }
    } else if (activeTab === "society") {
      if (rolesCache) {
        setMembersData(rolesCache);
        setFilteredData(rolesCache);
      } else {
        fetchRoles();
      }
    }
  }, [activeTab, employeesCache, rolesCache]);

  useEffect(() => {
    let result = membersData;

    if (activeTab === "service" && selectedCompany) {
      result = result.filter((m) => {
        const c = (m.companyName ?? "").toString().trim();
        return c && c.toLowerCase() === selectedCompany.toLowerCase();
      });
    }

    if (activeTab === "society" && selectedSociety) {
      result = result.filter((m) => {
        const s = (m.societyName ?? "").toString().trim();
        return s && s.toLowerCase() === selectedSociety.toLowerCase();
      });
    }

    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      result = result.filter((item) => {
        return Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredData(result);
  }, [activeTab, selectedCompany, selectedSociety, searchQuery, membersData]);


  const handleTabClick = async (tabId) => {
    setActiveTab(tabId);
    setSelectedCompany(null);
    setSelectedSociety(null);
    setSearchQuery("");
  };

  const handleCloseTab = (tabId, e) => {
    e.stopPropagation();
    if (tabs.length > 1) {
      setTabs(tabs.filter((tab) => tab.id !== tabId));
      if (activeTab === tabId) {
        setActiveTab(tabs[0].id);
      }
    }
  };

  const handleAddTab = () => {
    const newId = `tab-${tabs.length + 1}`;
    setTabs([
      ...tabs,
      {
        id: newId,
        label: `New Tab ${tabs.length + 1}`,
        icon: <FileText size={16} />,
      },
    ]);
    setActiveTab(newId);
  };

  const handleCompanyClick = (company) => {
    setSelectedSociety(null);
    if (selectedCompany === company) {
      setSelectedCompany(null);
    } else {
      setSelectedCompany(company);
    }
  };

  const handleSocietyClick = (society) => {
    setSelectedCompany(null);
    if (selectedSociety === society) {
      setSelectedSociety(null);
    } else {
      setSelectedSociety(society);
    }
  };

  const handleRefresh = async () => {
    if (activeTab === "service") {
      await fetchEmployees();
    } else {
      await fetchRoles();
    }
  };

  const formatCellValue = (val) => {
    if (val === null || val === undefined) return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return String(val);
  };

  const renderSocietyTable = () => {
    if (!filteredData || filteredData.length === 0) {
      return null;
    }

    const first = filteredData[0];
    const cols = Object.keys(first);

    return (
      <div className="archive-table-container">
        <table className="archive-data-table">
          <thead>
            <tr>
              {cols.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, idx) => (
              <tr key={row.id ?? row.employeeId ?? idx}>
                {cols.map((col) => (
                  <td key={col}>{formatCellValue(row[col])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="archive-container">
            <div className="browser-tabs-container">
              <div className="browser-tabs-wrapper">
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={`browser-tab ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    <div className="tab-content">
                      <span className="tab-icon">{tab.icon}</span>
                      <span className="tab-label">{tab.label}</span>
                      {tabs.length > 1 && (
                        <button
                          className="tab-close-btn"
                          onClick={(e) => handleCloseTab(tab.id, e)}
                          aria-label={`Close ${tab.label}`}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                    {activeTab === tab.id && (
                      <div className="tab-active-indicator" />
                    )}
                  </div>
                ))}
                <button
                  className="add-tab-btn"
                  onClick={handleAddTab}
                  aria-label="Add new tab"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="tab-actions">
                <button
                  className="tab-action-btn"
                  title="Refresh"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshCw
                    size={16}
                    className={loading ? "spin-animation" : ""}
                  />
                </button>
                <button className="tab-action-btn" title="Download">
                  <Download size={16} />
                </button>
              </div>
            </div>
            <div className="tab-content-area">
              <div className="tab-header">
                <div className="tab-title">
                  {activeTab === "service" ? (
                    <Building2 size={20} />
                  ) : (
                    <Users size={20} />
                  )}
                  <h2>
                    {activeTab === "service"
                      ? "Service Archive"
                      : "Society Archive"}
                  </h2>
                </div>

                <div className="tab-controls">
                  <div className="tab-search-container">
                    <div className="search-input-wrapper">
                      <Search className="search-icon" size={16} />
                      <input
                        type="text"
                        className="search-input"
                        placeholder={`Search ${activeTab} records...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="clear-search-btn"
                          onClick={() => setSearchQuery("")}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    className="tab-filter-btn"
                    onClick={() => {
                      setSelectedCompany(null);
                      setSelectedSociety(null);
                    }}
                  >
                    <Filter size={16} />
                    <span>Clear Filters</span>
                  </button>
                </div>
              </div>

              <div className="tab-main-content">
                <div className="filters-sidebar">
                  <div className="filters-header">
                    <h3>Filters</h3>
                    <button
                      className="clear-filters-btn"
                      onClick={() => {
                        setSelectedCompany(null);
                        setSelectedSociety(null);
                      }}
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="filters-list">
                    {activeTab === "service" ? (
                      <>
                        <div className="filter-section">
                          <h4>Companies ({companies.length})</h4>
                          <div className="filter-items">
                            <button
                              className={`filter-item ${
                                !selectedCompany ? "active" : ""
                              }`}
                              onClick={() => setSelectedCompany(null)}
                            >
                              All Companies
                            </button>
                            {companies.map((company) => (
                              <button
                                key={company}
                                className={`filter-item ${
                                  selectedCompany === company ? "active" : ""
                                }`}
                                onClick={() => handleCompanyClick(company)}
                              >
                                <Building2 size={14} />
                                <span>{company}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="filter-section">
                          <h4>Societies ({societies.length})</h4>
                          <div className="filter-items">
                            <button
                              className={`filter-item ${
                                !selectedSociety ? "active" : ""
                              }`}
                              onClick={() => setSelectedSociety(null)}
                            >
                              All Societies
                            </button>
                            {societies.map((society) => (
                              <button
                                key={society}
                                className={`filter-item ${
                                  selectedSociety === society ? "active" : ""
                                }`}
                                onClick={() => handleSocietyClick(society)}
                              >
                                <Users size={14} />
                                <span>{society}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="table-main-area">
                  {loading ? (
                    <div className="loading-state">
                      <RefreshCw size={48} className="spin-animation" />
                      <h3>Loading Data</h3>
                      <p>Please wait while we fetch the archive records...</p>
                    </div>
                  ) : error ? (
                    <div className="error-state">
                      <AlertCircle size={48} />
                      <h3>Unable to Load Data</h3>
                      <p>{error}</p>
                      <button
                        className="action-btn primary-btn"
                        onClick={handleRefresh}
                        style={{ marginTop: "16px" }}
                      >
                        <RefreshCw size={16} />
                        Try Again
                      </button>
                    </div>
                  ) : filteredData.length === 0 ? (
                    <div className="empty-state">
                      <FileText size={48} />
                      <h3>No Records Found</h3>
                      <p>
                        {activeTab === "service"
                          ? "No service records available."
                          : "No society records available."}
                      </p>
                      {searchQuery && (
                        <button
                          className="action-btn primary-btn"
                          onClick={() => setSearchQuery("")}
                          style={{ marginTop: "16px" }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  ) : activeTab === "service" ? (
                    <div className="archive-table-container">
                      <table className="archive-data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Designation</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Company</th>
                            <th>Address</th>
                            <th>Society Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredData.map((member, index) => (
                            <tr key={member.employeeId ?? index}>
                              <td>{member.employeeName ?? "-"}</td>
                              <td>{member.designation ?? "-"}</td>
                              <td>{member.employeeEmail ?? "-"}</td>
                              <td>{member.employeePhone ?? "-"}</td>
                              <td>{member.companyName ?? "-"}</td>
                              <td>{member.employeeAddress ?? "-"}</td>
                              <td>{member.societyName ?? "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    renderSocietyTable()
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Archive;
